export const DAY_NAMES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export interface PeriodRange {
  key: string;
  start: Date;
  end: Date;
}

export const normalizeDateString = (date: string) => {
  if (!date) return "";
  return date.includes("T") ? date.split("T")[0] : date;
};

export const getDayIndex = (date: string) => {
  const parsed = new Date(`${normalizeDateString(date)}T00:00:00`);
  const day = parsed.getDay();

  return day === 0 ? 6 : day - 1;
};

export const buildPeriodsDays = (days: any[]) => {
  const period = Array(7).fill(null);

  days.forEach((day) => {
    const index = getDayIndex(day.date);
    period[index] = day;
  });

  return period;
};

export const buildDayColumns = (referenceDate?: string) => {
  if (!referenceDate) {
    return DAY_NAMES.map((name, i) => ({
      isSortable: false,
      name: `day-${i}`,
      title: name,
    }));
  }

  const parsed = new Date(`${normalizeDateString(referenceDate)}T00:00:00`);
  const dayIndex = getDayIndex(referenceDate);

  const monday = new Date(parsed);
  monday.setDate(parsed.getDate() - dayIndex);

  const columns = [];

  for (let i = 0; i < 7; i++) {
    const current = new Date(monday);
    current.setDate(monday.getDate() + i);

    columns.push({
      isSortable: false,
      name: `day-${i}`,
      title: `${DAY_NAMES[i]} ${current.getDate()}/${current.getMonth() + 1}`,
    });
  }

  return columns;
};

const toDateAtMidnight = (date: string) =>
  new Date(`${normalizeDateString(date)}T00:00:00`);

export const getMonday = (date: Date) => {
  const monday = new Date(date);
  const day = monday.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

export const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildWeekRangeFromDay = (dateValue: string): PeriodRange => {
  const source = toDateAtMidnight(dateValue);
  const start = getMonday(source);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    key: formatDateKey(start),
    start,
    end,
  };
};

export const buildWeekRangesFromDates = (dates: string[]) => {
  const weekMap = new Map<string, PeriodRange>();

  dates.forEach((dateValue) => {
    const week = buildWeekRangeFromDay(dateValue);
    if (!weekMap.has(week.key)) {
      weekMap.set(week.key, week);
    }
  });

  return Array.from(weekMap.values()).sort(
    (a, b) => a.start.getTime() - b.start.getTime(),
  );
};

export const formatDateLabel = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
};

export const formatDateLabelWithDay = (date: Date | string) => {
  const parsed =
    typeof date === "string"
      ? new Date(`${normalizeDateString(date)}T00:00:00`)
      : date;
  const dayIndex = parsed.getDay();
  const dayName = DAY_NAMES[dayIndex === 0 ? 6 : dayIndex - 1];
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  return `${dayName} ${day}/${month}`;
};

export const buildWeekRangesFromFallback = (
  startDate?: string,
  endDate?: string,
): PeriodRange[] => {
  if (!startDate || !endDate) return [];

  const start = getMonday(
    new Date(`${normalizeDateString(startDate)}T00:00:00`),
  );
  const end = new Date(`${normalizeDateString(endDate)}T00:00:00`);

  const ranges: PeriodRange[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const weekStart = new Date(cursor);
    const weekEnd = new Date(cursor);
    weekEnd.setDate(weekStart.getDate() + 6);

    ranges.push({
      key: weekStart.toISOString().slice(0, 10),
      start: weekStart,
      end: weekEnd,
    });

    cursor.setDate(cursor.getDate() + 7);
  }

  return ranges;
};
